import sortBy from 'lodash/sortBy';
import type {
  Card,
  CoverSheetConfig,
  CoverSheetConfigCard,
  CoverSheetConfigSection,
  DefaultCoverSheetConfig,
  Field,
  LocaleOverrides,
  Section,
} from '../../types';
import { applyOverrides } from './applyOverrides';

const transformCoverSheetConfigCard = (card: CoverSheetConfigCard): Card => {
  const { order: _order, ...filteredCard } = card;

  return {
    ...filteredCard,
    fields: Object.values(filteredCard.fields).filter(
      (field): field is Field => field !== null,
    ),
  };
};

const transformCoverSheetSection = (
  section: CoverSheetConfigSection,
): Section => {
  const cardsWithOrder = Object.values(section.cards)
    .filter((card): card is CoverSheetConfigCard => card !== null)
    .map((card, index) => ({
      ...card,
      order: card.order ?? index,
    }));

  return {
    ...section,
    cards: sortBy(cardsWithOrder, ['order']).map(transformCoverSheetConfigCard),
  };
};

/**
 * Build the derived coversheet config for a locale.
 * @param baseConfig Base configuration for interaction type
 * @param overrides Locale overrides
 * @returns Merged coversheet config derived from base config and any locale overrides
 */
export function buildCoverSheetConfig<
  T extends DefaultCoverSheetConfig,
  K extends LocaleOverrides<T>,
>(baseConfig: T, overrides: K): CoverSheetConfig {
  const mergedConfig = applyOverrides(baseConfig, overrides);

  return {
    ...mergedConfig,
    sections: Object.values(mergedConfig.sections).map(
      transformCoverSheetSection,
    ),
  };
}
